<template>
  <div
    id="schedule-report-form"
    :class="{ 'has-file-hovering': hasFileHovering }"
    @dragenter.prevent="hasFileHovering = true"
    @dragover.prevent="hasFileHovering = true"
    @dragleave.prevent="hasFileHovering = false"
    @drop.prevent="onFileDrop"
  >
    <div id="schedule-report-items" class="pt-5">
      <div id="schedule-report-items-bottom" ref="scheduleReportItemsBottom"></div>
      <schedule-report-item-card
        v-for="scheduleReportItem in scheduleReportItems"
        :key="scheduleReportItem.id"
        :schedule-report-item="scheduleReportItem"
        :is-editing="newReportItem.id === scheduleReportItem.id"
        can-edit
        @action:delete="deleteScheduleReportItem(scheduleReportItem)"
        @action:edit="editScheduleReportItem(scheduleReportItem)"
      />
      <div id="schedule-report-items-top" ref="scheduleReportItemsTop" class="pb-5">
        <div v-if="loadingScheduleReportItems" v-loading="loadingScheduleReportItems" class="pb-5"></div>
        <base-button
          v-else-if="nextPage < totalPages"
          type="success"
          @click="getScheduleReportItems"
        >
          Carregar mais
        </base-button>
      </div>
    </div>
    <div id="schedule-report-item-form-holder">
      <schedule-report-items-form
        v-model="newReportItem"
        @create="createScheduleReportItem"
        @edit="updateScheduleReportItem"
        @cancel-edit="newReportItem = {}"
        :loading="loadingNewReportItem"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed, onMounted, watch, nextTick } from "vue";
import axios from "@/backend/axios/index.js";
import ScheduleReportItemsForm from "@/components/ScheduleReportItem/ScheduleReportItemForm.vue";
import ScheduleReportItemCard from "@/components/ScheduleReportItem/ScheduleReportItemCard.vue";
import swal from "sweetalert2";

const emit = defineEmits(["input", "search"]);

const props = defineProps({
  value: {
    type: Object,
    required: true,
  },
})

const scheduleReport = computed({
  get: () => props.value,
  set: (value) => emit("input", value),
});

const scheduleReportItems = computed({
  get: () => scheduleReport.value.schedule_report_items || [],
  set: (value) => {
    scheduleReport.value.schedule_report_items = value;
    emit("input", scheduleReport.value);
  },
})
const loadingScheduleReportItems = ref(false);
const getScheduleReportItems = async () => {
  if (!scheduleReport.value.id) return;

  loadingScheduleReportItems.value = true;
  try {
    const response = await axios.get(`/api/v1/schedule_reports/${scheduleReport.value.id}/schedule_report_items`, {
      params: {
        q: {
          schedule_report_id: scheduleReport.value.id,
        },
        page: nextPage.value,
        until: currentDate.value,
        per_page: 10
      },
    });

    if (nextPage.value === 1) {
      scheduleReportItems.value = response.data["schedule_report_items"] || [];
      nextTick(() => {
        scrollToReportFormBottom();
      });
    } else {
      scheduleReportItems.value.push(...(response.data["schedule_report_items"] || []));
    }

    if (response.data.pagination_params) {
      nextPage.value = (response.data.pagination_params.page || 1) + 1;
      totalPages.value = (response.data.pagination_params.total || 0) / (response.data.pagination_params.per_page || 10);
    }
  } catch (error) {
    console.warn("[ScheduleReportForm][getScheduleReportItems]", error);
  }
  loadingScheduleReportItems.value = false;
};

// Scroll to bottom of the form
const scheduleReportItemsBottom = ref(null);

const scrollToReportFormBottom = () => {
  if (!scheduleReportItemsBottom.value) return;

  nextTick(() => {
    scheduleReportItemsBottom.value.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });
  })
};

watch(scheduleReport, () => {
  resetPagination();
  scrollToReportFormBottom();
  getScheduleReportItems();
});

// new report item logic

const newReportItem = ref({});
const loadingNewReportItem = ref(false);

const newReportItemFormData = computed(() => {
  const formData = new FormData();

  for (const key in newReportItem.value) {
    if (newReportItem.value.hasOwnProperty(key)) {
      if (key === 'media') continue;

      const value = newReportItem.value[key];
      formData.append(`schedule_report_item[${key}]`, value);
    }
  }

  if (newReportItem.value.media && newReportItem.value.media.length > 0) {
    for (const media of newReportItem.value.media) {
      if (media.signed_id) {
        formData.append('schedule_report_item[media][]', media.signed_id);
      } else {
        formData.append('schedule_report_item[media][]', media);
      }
    }
  }

  return formData;
});

const createScheduleReportItem = async () => {
  if (loadingNewReportItem.value)   return;
  if (!scheduleReport.value.id)     return;
  if (!newReportItem.value.content) return;

  loadingNewReportItem.value = true;
  console.log("[ScheduleReportForm][createScheduleReportItem]", newReportItem.value);
  try {
    const response = await axios.post(`/api/v1/schedule_reports/${scheduleReport.value.id}/schedule_report_items`, newReportItemFormData.value);

    scheduleReportItems.value = [response.data, ...scheduleReportItems.value];
    newReportItem.value = {};

    nextTick(() => {
      scrollToReportFormBottom();
    });
  } catch (error) {
    console.warn("[ScheduleReportForm][createScheduleReportItem]", error);
  }
  loadingNewReportItem.value = false;
};

const updateScheduleReportItem = async (scheduleReportItem) => {
  if (loadingNewReportItem.value)   return;
  if (!scheduleReport.value.id)     return;
  if (!newReportItem.value.content) return;

  loadingNewReportItem.value = true;
  console.log("[ScheduleReportForm][updateScheduleReportItem] scheduleReportItem", newReportItem.value);

  try {
    const response = await axios.put(`/api/v1/schedule_reports/${scheduleReport.value.id}/schedule_report_items/${scheduleReportItem.id}`, newReportItemFormData.value);

    scheduleReportItems.value = scheduleReportItems.value.map((item) => {
      if (item.id === response.data.id) {
        return response.data;
      }

      return item;
    });

    newReportItem.value = {};
  } catch (error) {
    console.warn("[ScheduleReportForm][updateScheduleReportItem]", error);
  }
  loadingNewReportItem.value = false;
};

const deleteScheduleReportItem = async (scheduleReportItem) => {
  const result = await swal.fire({
    title: "Tem certeza?",
    text: "Você não poderá desfazer esta ação!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sim, deletar!",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#dc3545",
    cancelButtonColor: "#6c757d",
  })

  if (!result.isConfirmed) return

  console.debug("[ScheduleReportForm][deleteScheduleReportItem] scheduleReportItem", scheduleReportItem);

  try {
    await axios.delete(`/api/v1/schedule_reports/${scheduleReport.value.id}/schedule_report_items/${scheduleReportItem.id}`)
    scheduleReportItems.value = scheduleReportItems.value.filter((item) => item.id !== scheduleReportItem.id);
  } catch (error) {
    swal.fire({
      title: "Erro!",
      text: "Não foi possível deletar o item do relatório de visita.",
      icon: "error",
      confirmButtonText: "Ok",
      confirmButtonColor: "#dc3545",
    })
  }
};

const editScheduleReportItem = async (scheduleReportItem) => {
  console.debug("[ScheduleReportForm][editScheduleReportItem] scheduleReportItem", scheduleReportItem);
  newReportItem.value = Object.assign({}, scheduleReportItem);
};

// Handle file drop
const hasFileHovering = ref(false);
const onFileDrop = (event) => {
  console.debug("[ScheduleReportForm][onFileDrop] file dropped", event);
  hasFileHovering.value = false;

  const files = event.dataTransfer.files || [];

  if (!files.length) return;
  if (!newReportItem.value.media) newReportItem.value.media = [];

  newReportItem.value.media = [...newReportItem.value.media, ...event.dataTransfer.files]
  newReportItem.value = { ...newReportItem.value };
};

// Pagination logic
const scheduleReportItemsTop = ref(null);
const nextPage = ref(1);
const currentDate = new Date();
const totalPages = ref(1);
const resetPagination = () => {
  nextPage.value = 1;
  totalPages.value = 1;
  currentDate.value = new Date();
};
watch(scheduleReportItems, (val) => {
  if (val.length != 0) return;

  resetPagination();
});

onMounted(() => {
  getScheduleReportItems();
  scrollToReportFormBottom();
});
</script>

<style lang="scss">
#schedule-report-form {
  height: calc(100% - 48px);
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  overflow-y: auto;
  padding: 0;

  &.has-file-hovering {
    opacity: 0.3;

    &::before {
      content: "Solte o arquivo aqui";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 1.5rem;
      font-weight: bold;
      border: 4px dashed #000;
      padding: 20px;
      border-radius: 10px;
      color: #000;
    }
  }
}

#schedule-report-items {
  height: calc(100vh - 310px - 92px);
  overflow-y: auto;
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  padding: 0 20px;
}

#schedule-report-item-form-holder {
  width: 100%;
  padding: 10px 26px 10px 26px;
}

#schedule-report-items-bottom {
  height: 1px;
  width: 100%;
  background-color: transparent;
}

#teste {
  position: absolute;
  bottom: 0;
}
</style>
